import { useState } from 'react';
import type { DotplotCell } from '@/types/analytics';
import { AUTOMATION_EVENT, dotSizePx } from '@/lib/analytics/dotplot';

/**
 * Hollow gray marker for an automation-only day (`total: 0, automation > 0`) —
 * background/machine message volume with no human engagement. Deliberately
 * never a filled catalog color so engaged-vs-passive users read at a glance.
 * Self-contained (owns its own tooltip) so it drops into any grid cell without
 * threading tooltip state through the parent.
 */
export function AutomationDot({
  cell,
  day,
  title,
}: {
  cell: DotplotCell;
  day: string;
  /** Optional tooltip header, e.g. the user's display name. */
  title?: string;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const automation = cell.automation ?? 0;
  const size = dotSizePx(automation);

  return (
    <>
      <span
        tabIndex={0}
        role="img"
        aria-label={`${day}: ${automation} automation message${automation === 1 ? '' : 's'} (no human activity)`}
        onMouseEnter={(e) => setRect(e.currentTarget.getBoundingClientRect())}
        onMouseLeave={() => setRect(null)}
        onFocus={(e) => setRect(e.currentTarget.getBoundingClientRect())}
        onBlur={() => setRect(null)}
        className="inline-block cursor-default rounded-full border-[1.5px] border-muted-foreground bg-transparent align-middle outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        style={{ width: size, height: size }}
      />
      {rect && <AutomationTooltip rect={rect} automation={automation} day={day} title={title} />}
    </>
  );
}

function AutomationTooltip({
  rect,
  automation,
  day,
  title,
}: {
  rect: DOMRect;
  automation: number;
  day: string;
  title?: string;
}) {
  const placeBelow = rect.top - 8 < 120;
  const top = placeBelow ? rect.bottom + 8 : rect.top - 8;
  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 max-w-[300px] -translate-x-1/2 rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg"
      style={{
        left: rect.left + rect.width / 2,
        top,
        transform: `translate(-50%, ${placeBelow ? '0' : '-100%'})`,
      }}
    >
      {title && <div className="font-medium text-foreground">{title}</div>}
      <div className="mt-0.5 text-muted-foreground">
        {day} · automation only
      </div>
      <div className="mt-1 flex items-center justify-between gap-3">
        <span className="text-muted-foreground">{AUTOMATION_EVENT}</span>
        <span className="font-mono tabular-nums text-foreground">{automation}</span>
      </div>
    </div>
  );
}

export default AutomationDot;
